import { createContext, useContext, useState, type ReactNode } from 'react';
import { useUnauthRumAction } from '../hooks/useUnauthRumAction';

/**
 * Triggers that open the guest signup modal — mirrors the spec's gated-action table
 * (Item 2, lines 56-63 of care-seeker-pre-login-experience_spec.md).
 *
 * Captured here as plain strings so we can later attribute the conversion event
 * back to the gate that triggered signup (spec: signupTrigger column on User).
 */
export type GuestSignupTrigger =
  | 'locked_tab_click'
  | 'banner_cta'
  | 'provider_profile_click'
  | 'resource_cap_hit'
  | 'action_button_click';

export type GuestSignupContext = {
  trigger: GuestSignupTrigger;
  /** Optional entity id (provider id, resource id, tab name) for deep-linking after signup. */
  triggerEntityId?: string;
  /**
   * When trigger is `provider_profile_click`, distinguishes SP (→ /providers/:id)
   * from FA (→ /advisors/:id) so the post-signup deep link lands on the right route.
   */
  providerType?: 'SERVICE_PROVIDER' | 'FINANCIAL_ADVISOR';
  /** Pre-fills the signup email (e.g. a longevity-assessment client whose email is known). */
  email?: string;
  /**
   * Skip the "which advisor?" step when the advisor is already known (e.g. a
   * longevity-assessment client — the advisor link is established by the claim).
   */
  skipAdvisorStep?: boolean;
};

type ModalState =
  | { isOpen: false }
  | ({ isOpen: true } & GuestSignupContext);

type GuestSignupModalContextValue = {
  state: ModalState;
  openModal: (context: GuestSignupContext) => void;
  closeModal: () => void;
};

const GuestSignupModalContext = createContext<GuestSignupModalContextValue | null>(null);

export const GuestSignupModalProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ModalState>({ isOpen: false });
  const trackAction = useUnauthRumAction();

  const openModal = (context: GuestSignupContext) => {
    // Fires on every modal open regardless of trigger — granular trigger-specific
    // events (locked_tab_click, banner_click, etc.) are fired separately by callers.
    trackAction('unauth.signup_modal_open', {
      trigger: context.trigger,
      triggerEntityId: context.triggerEntityId,
    });
    setState({ isOpen: true, ...context });
  };

  const closeModal = () => {
    setState({ isOpen: false });
  };

  return (
    <GuestSignupModalContext.Provider value={{ state, openModal, closeModal }}>
      {children}
    </GuestSignupModalContext.Provider>
  );
};

/**
 * Returns the modal controller when called inside GuestSignupModalProvider (i.e. for guests
 * inside the pre-login shell). For authenticated users (rendered in AppLayout — no provider),
 * returns a no-op controller so shared components like ProviderCard can call this hook safely
 * regardless of mount context.
 */
const NOOP_VALUE: GuestSignupModalContextValue = {
  state: { isOpen: false },
  openModal: () => {},
  closeModal: () => {},
};

export const useGuestSignupModal = (): GuestSignupModalContextValue => {
  return useContext(GuestSignupModalContext) ?? NOOP_VALUE;
};
